// Offer Acceptance Service
// Records offer acceptance and assigns one-time codes to member accounts

import { NESPRESSO_OFFERS, getDiscountedPrice } from '../config/offers';

export interface AcceptedOffer {
  offerId: string;
  offerName: string;
  originalPrice: number;
  discountedPrice: number;
  discountPercent: number;
  redemptionCode: string;
  acceptedAt: Date;
  expiresAt: Date;
}

// Generate a unique redemption code
const generateRedemptionCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'NESP-';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Storage key for demo persistence
const STORAGE_KEY = 'nespresso-accepted-offers';

// Get all accepted offers from storage
export const getAcceptedOffers = (): AcceptedOffer[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const offers = JSON.parse(stored);
      return offers.map((o: AcceptedOffer) => ({
        ...o,
        acceptedAt: new Date(o.acceptedAt),
        expiresAt: new Date(o.expiresAt),
      }));
    }
  } catch (e) {
    console.error('Failed to load accepted offers:', e);
  }
  return [];
};

// Save accepted offers to storage
const saveAcceptedOffers = (offers: AcceptedOffer[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(offers));
  } catch (e) {
    console.error('Failed to save accepted offers:', e);
  }
};

// Accept an offer and generate a redemption code
export const acceptOffer = async (offerId: string): Promise<AcceptedOffer | null> => {
  const offer = NESPRESSO_OFFERS.find(o => o.id === offerId);
  if (!offer) {
    console.error('Offer not found:', offerId);
    return null;
  }

  // Simulate API delay for demo
  await new Promise(resolve => setTimeout(resolve, 500));

  const acceptedOffer: AcceptedOffer = {
    offerId: offer.id,
    offerName: offer.name,
    originalPrice: offer.originalPrice,
    discountedPrice: getDiscountedPrice(offer),
    discountPercent: offer.discountPercent,
    redemptionCode: generateRedemptionCode(),
    acceptedAt: new Date(),
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
  };

  // Store the accepted offer
  const existingOffers = getAcceptedOffers();
  saveAcceptedOffers([...existingOffers, acceptedOffer]);

  return acceptedOffer;
};

// Get a specific accepted offer by ID
export const getAcceptedOffer = (offerId: string): AcceptedOffer | null => {
  const offers = getAcceptedOffers();
  return offers.find(o => o.offerId === offerId) || null;
};

// Clear all accepted offers (for demo reset)
export const clearAcceptedOffers = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};
